import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { isEmpty, isEqual } from "lodash";
import { PeopleView } from "@/entities/people/types";
import { usePeopleTable } from "@/services/hooks/entities/person/usePeopleTable";
import PeopleTable from "@/components/layouts/People/PeopleTable";
import { getFullName } from "@/entities/people/helpers";
import EmptyState from "./components/EmptyState";
import PeopleCardsGrid from "./components/PeopleCardsGrid";
import NewPersonButton from "./components/NewPersonButton";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { ColumnData } from "@/entities/dataTables/types";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import {
  saveConfigToTempView,
  getTempView
} from "@/entities/dataTables/helpers";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import Tabs from "@/components/controls/Tabs";
import { Cards, Table } from "@dreamhub/designsystem/icons";

const TABLE_ID = "companyPeopleTab-table";

interface Props {
  companyId: string;
}

const CompanyPeopleTab: React.FC<Props> = ({ companyId }) => {
  const { t } = useTranslation();

  const hiddenColumns = ["companyId"];
  const initialFilters = useMemo(() => ({ companyId }), [companyId]);

  const [tabValue, setTabValue] = useState(PeopleView.TABLE.toString());
  const [isCreatePersonModalOpen, setIsCreatePersonModalOpen] = useState(false);
  const [selectedView] = useState<ViewDataConfiguration>(() => ({
    ...EmptyView(ViewType.PEOPLE_TABLE),
    id: TABLE_ID,
    isOpen: true
  }));

  const [columnsState, setColumnsState] = useState<ColumnData[]>(() => {
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns ?? [];
  });

  const handleColumnsChange = useCallback(
    (updatedColumns?: ColumnData[]) => {
      if (updatedColumns) {
        setColumnsState(updatedColumns);
        saveConfigToTempView(selectedView, {
          columns: updatedColumns
        });
      }
    },
    [selectedView]
  );

  useEffect(() => {
    const existingView = getTempView(selectedView);
    if (!existingView) {
      saveConfigToTempView(selectedView, {
        filters: {},
        columns: []
      });
    }
  }, []);

  const {
    people,
    reportsToDataMap,
    sorting,
    onSortingChange,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    peopleFilterOptions,
    peopleSortByOptions,
    handleLoadMore,
    resetPage,
    isError,
    refetch
  } = usePeopleTable({
    hiddenColumns,
    initialFilters,
    enabled: true
  });

  const getReportsToPersonName = (personId: string | null | undefined) => {
    if (isEmpty(personId)) {
      return "";
    }
    const person = reportsToDataMap[personId as string];
    if (!person) return t("notFound");
    return getFullName(person);
  };

  const renderContent = () => {
    if (
      !isLoading &&
      !isError &&
      people?.length === 0 &&
      isEmpty(searchTerm) &&
      isEqual(filters, initialFilters)
    ) {
      return (
        <EmptyState
          onAddPeople={() => setIsCreatePersonModalOpen(true)}
          entityType={EntityType.COMPANY}
        />
      );
    }

    return (
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div>
          <ToolbarActions
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filters={filters}
            setFilters={setFilters}
            filterOptions={peopleFilterOptions}
            sorting={sorting}
            onSortingChange={onSortingChange}
            sortByOptions={peopleSortByOptions}
            defaultSortByValue="fullName"
            showTableColumns={false}
            showDisplayButton={tabValue === PeopleView.TABLE.toString()}
            onColumnsChange={handleColumnsChange}
            tableId={TABLE_ID}
            className="p-4 border-none"
            baseFilters={initialFilters}
            leftActions={
              <Tabs
                options={{
                  [PeopleView.TABLE.toString()]: <Table />,
                  [PeopleView.CARDS.toString()]: <Cards />
                }}
                defaultValue={tabValue}
                valueSelected={(value) => setTabValue(value)}
              />
            }
            rightActions={
              <NewPersonButton
                onClick={() => setIsCreatePersonModalOpen(true)}
                isDealMode={false}
              />
            }
          />
        </div>
        <div className="flex-1 overflow-hidden">
          {tabValue === PeopleView.TABLE.toString() ? (
            <PeopleTable
              people={people}
              sorting={sorting}
              onSortingChange={onSortingChange}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={handleLoadMore}
              hiddenColumns={hiddenColumns}
              className="h-[66vh]"
              tableId={TABLE_ID}
              selectedView={selectedView}
              columnsState={columnsState}
              onColumnsChange={handleColumnsChange}
              hasActiveFilters={!isEqual(filters, initialFilters)}
              onClearFilters={() => setFilters(initialFilters)}
              onClearSearch={() => setSearchTerm("")}
              isError={isError}
              onRetry={refetch}
            />
          ) : (
            <PeopleCardsGrid
              people={people}
              getReportsToPersonName={getReportsToPersonName}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderContent()}
      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        setIsOpen={setIsCreatePersonModalOpen}
        resetPage={resetPage}
        companyId={companyId}
      />
    </>
  );
};

export default CompanyPeopleTab;
